import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { analyzeBusiness, calculateBusinessValuation } from '../../api/valuation';
import logger from '../../utils/logger';
import crmCapture from '../../utils/crmCapture';

// Step Components
import { ErrorState } from './ErrorState';
import FinancialDataModal from './FinancialDataModal';
import { ProgressStepper, MiniProgressStepper } from './ProgressStepper';
import { StepOne, StepFour } from './steps';
import UnifiedLoadingScreen from './UnifiedLoadingScreen';
import { useProgress } from './useProgress';

export default function ValuationSection() {
  // URL Parameters
  const [searchParams] = useSearchParams();

  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [pendingWebsite, setPendingWebsite] = useState('');

  // Use our improved progress hook
  const { resetProgress } = useProgress();

  const [data, setData] = useState({
    website: '',
    businessAnalysis: null,
    financialInputs: {
      preTaxProfit: '',
      growthTrend: '',
      recurringRevenue: 0,
    },
    valuation: null,
  });

  // Initialize with URL parameter - FIXED: Better dependency management
  useEffect(() => {
    const websiteParam = searchParams.get('website');
    const autoStart = searchParams.get('autoStart');

    // Only process URL parameters on initial load (step 1) and if we haven't already processed them
    if (websiteParam && currentStep === 1 && !data.website && !isAnalyzing) {
      const decodedWebsite = decodeURIComponent(websiteParam);
      logger.log('Processing URL parameters:', { websiteParam: decodedWebsite, autoStart });

      setData((prev) => ({
        ...prev,
        website: decodedWebsite,
      }));

      // Auto-start analysis if autoStart parameter is present
      if (autoStart === 'true' && !data.businessAnalysis) {
        // Use setTimeout to ensure state is updated before calling handleAnalyzeBusiness
        setTimeout(() => {
          logger.log('Auto-starting for:', decodedWebsite);
          handleWebsiteSubmit(decodedWebsite);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentStep, data.website, isAnalyzing, data.businessAnalysis]);



  // Step Navigation
  const prevStep = () => setCurrentStep((prev) => prev - 1);
  const resetFlow = () => {
    logger.log('resetFlow called - clearing all data');
    setCurrentStep(1);
    resetProgress();
    setError(null);
    setData({
      website: '',
      businessAnalysis: null,
      financialInputs: {
        preTaxProfit: '',
        growthTrend: '',
        recurringRevenue: 0,
      },
      valuation: null,
    });
  };

  // New handler that shows the financial modal first
  const handleWebsiteSubmit = (website) => {
    setPendingWebsite(website);
    setShowFinancialModal(true);
  };

  // Handler for when financial data is submitted from modal
  const handleFinancialDataSubmit = async (financialInputs) => {
    setShowFinancialModal(false);

    // Save financial inputs
    setData((prev) => ({
      ...prev,
      financialInputs,
      website: pendingWebsite,
    }));

    // Start both analysis and calculation
    await handleAnalyzeAndCalculate(pendingWebsite, financialInputs);
  };

  // Combined handler for analysis and calculation
  const handleAnalyzeAndCalculate = async (website, financialInputs) => {
    setError(null);
    setIsAnalyzing(true);
    setIsLoading(true);
    resetProgress();

    try {
      // Start both API calls in parallel
      const analysisPromise = analyzeBusiness(website);
      
      // Set a minimum loading time to ensure smooth animations (42 seconds total)
      const minimumLoadingTime = 42000;
      const startTime = Date.now();

      // Wait for the analysis to complete
      const analysis = await analysisPromise;

      // Store the analysis
      setData((prev) => ({
        ...prev,
        businessAnalysis: analysis,
      }));

      // Now calculate valuation
      const valuationResult = await calculateBusinessValuation({
        ...financialInputs,
        businessAnalysis: analysis,
      });

      // Calculate remaining time to meet minimum loading duration
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);

      // Wait for the remaining time if needed
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      // Store valuation and move to results
      setData((prev) => ({
        ...prev,
        valuation: valuationResult,
      }));
      
      // Capture valuation data for CRM directly (Netlify SPA - no backend)
      try {
        const valuationId = crmCapture.generateValuationId();
        await crmCapture.captureValuationWithContact({
          valuationId,
          website: pendingWebsite,
          businessAnalysis: analysis,
          financialInputs,
          valuation: valuationResult
        });
        logger.log('Valuation data captured for CRM');
      } catch (crmError) {
        logger.warn('CRM capture failed (non-blocking):', crmError.message);
        // Don't block the user experience if CRM fails
      }
      
      setCurrentStep(2);
      setIsAnalyzing(false);
      setIsLoading(false);
      resetProgress();
      
      logger.log('Successfully completed analysis and valuation');
    } catch (err) {
      logger.error('Analysis/Valuation failed:', err);
      setError(err.message || 'Failed to complete analysis. Please try again.');
      setIsAnalyzing(false);
      setIsLoading(false);
      resetProgress();
    }
  };





  // Render Current Step
  const renderStep = () => {
    // Show error state if there's an error
    if (error) {
      return (
        <ErrorState
          error={error}
          onRetry={() => {
            setError(null);
            // Retry the last action based on current step
            if (currentStep === 1 && pendingWebsite) {
              handleWebsiteSubmit(pendingWebsite);
            } else if (currentStep === 1 && data.website) {
              handleWebsiteSubmit(data.website);
            }
          }}
          onReset={resetFlow}
        />
      );
    }

    if (isAnalyzing || isLoading) {
      return (
        <UnifiedLoadingScreen 
          website={pendingWebsite || data.website}
          financialData={data.financialInputs}
        />
      );
    }

    // Render current step content


    switch (currentStep) {
      case 1:
        return (
          <>
            <StepOne website={data.website} onAnalyze={handleWebsiteSubmit} isLoading={isLoading} />
            <FinancialDataModal
              isOpen={showFinancialModal}
              onSubmit={handleFinancialDataSubmit}
              onClose={() => setShowFinancialModal(false)}
            />
          </>
        );
      case 2:
        logger.log('Rendering StepFour with data:', {
          valuation: data.valuation,
          businessAnalysis: data.businessAnalysis,
          currentStep,
          fullData: data,
        });

        // Add safety check to prevent rendering without valuation data
        if (!data.valuation) {
          logger.error('StepFour rendered without valuation data! Current state:', data);
          return (
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading your valuation...</h2>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">If this persists, please start over.</p>
              <button onClick={resetFlow} className="btn-secondary mt-4">
                Start Over
              </button>
            </div>
          );
        }

        return (
          <StepFour
            valuation={data.valuation}
            businessAnalysis={data.businessAnalysis}
            onReset={resetFlow}
            onBack={prevStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-white)' }}>
      {/* Main Content */}
      <div className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Progress Stepper - Show on desktop */}
          <div className="hidden lg:block mb-12 lg:mb-16">
            <ProgressStepper
              currentStep={currentStep}
              isAnalyzing={isAnalyzing}
              isLoading={isLoading}
            />
          </div>

          {/* Mini Progress - Show on mobile */}
          <div className="lg:hidden">
            <MiniProgressStepper currentStep={currentStep} />
          </div>

          {/* Step Content - Add top margin to prevent overlap */}
          <div className="mt-8 sm:mt-10 lg:mt-0">{renderStep()}</div>
        </div>
      </div>
    </div>
  );
}
